import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Gift, Award, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const PremiumFeatures: React.FC = () => {
  const { t } = useTranslation();

  const features = [
    {
      icon: Heart,
      title: t('features.handmade.title'),
      description: t('features.handmade.description'),
      gradient: 'from-rose-400 to-pink-600'
    },
    {
      icon: Gift,
      title: t('features.custom.title'),
      description: t('features.custom.description'),
      gradient: 'from-purple-400 to-purple-600'
    },
    {
      icon: Sparkles,
      title: t('features.premium.title'),
      description: t('features.premium.description'),
      gradient: 'from-amber-400 to-yellow-600'
    },
    {
      icon: Award,
      title: t('features.guarantee.title'),
      description: t('features.guarantee.description'),
      gradient: 'from-blue-400 to-blue-600'
    }
  ];

  return (
    <section className="py-10 bg-white">
      <div className="container mx-auto px-4">
        {/* Compact Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-6 md:mb-8"
        >
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-serif font-bold text-gray-900 mb-3">
            {t('features.title')}
          </h2>
          <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
            {t('features.subtitle')}
          </p>
        </motion.div>

        {/* Compact Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-4">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="group"
            >
              <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm hover:shadow-md transition-all duration-300 text-center h-full">
                {/* Compact Icon */}
                <div className={`w-12 h-12 md:w-14 md:h-14 mx-auto rounded-full bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-3 group-hover:scale-105 transition-transform duration-300`}>
                  <feature.icon className="w-6 h-6 md:w-7 md:h-7 text-white" />
                </div>

                {/* Compact Content */}
                <h3 className="text-lg md:text-xl font-serif font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PremiumFeatures;
